import React from "react";
import PropTypes from "prop-types";
import {
  TextField,
  Typography,
  Box,
  FormControlLabel,
  RadioGroup,
  Radio,
  Button,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { Controller } from "react-hook-form";

const canViewCreditReport = (role, pii_access) =>
  role === "carrier_team_member" ||
  (role === "super_user" && pii_access) ||
  (role === "internal_user" && pii_access);

const hasDocument = (fileName) =>
  fileName && fileName !== "No file uploaded" && String(fileName).trim() !== "";

const DocumentViewerRow = ({ label, fileName, fileLink }) => (
  <Box>
    <Typography variant="body1" className="mb-2">
      {label}
    </Typography>
    {hasDocument(fileName) ? (
      <Button
        variant="outlined"
        color="secondary"
        href={fileLink}
        target="_blank"
        rel="noopener noreferrer"
        fullWidth
        sx={{ mt: 1 }}
      >
        View Document
      </Button>
    ) : (
      <Typography variant="body2" color="textSecondary" mt={1}>
        No document available
      </Typography>
    )}
    {hasDocument(fileName) && (
      <Typography variant="body2" color="textSecondary" mt={1}>
        File name: {fileName}
      </Typography>
    )}
  </Box>
);

DocumentViewerRow.propTypes = {
  label: PropTypes.string.isRequired,
  fileName: PropTypes.string,
  fileLink: PropTypes.string,
};

const BondRetrievalDetailedInformationBase = ({
  config,
  isDisabled,
  control,
  errors,
  watch,
  role,
  pii_access,
}) => {
  const { title, documentsHeading, showBondTerm, bondTermOptions, documentRows } = config;

  return (
    <Box className="grid grid-cols-1 gap-4">
      <Typography variant="h5" className="font-bold mb-4">
        {title}
      </Typography>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Typography>Have you been bankrupt or insolvent?</Typography>
          <Controller
            name="bankrupt"
            control={control}
            defaultValue="No"
            render={({ field }) => (
              <RadioGroup disabled={isDisabled} {...field} row>
                <FormControlLabel disabled={isDisabled} value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel disabled={isDisabled} value="No" control={<Radio />} label="No" />
              </RadioGroup>
            )}
          />
        </Grid>

        {watch("bankrupt") === "Yes" && (
          <Grid size={{ xs: 12 }}>
            <Controller
              name="bankruptExplanation"
              control={control}
              defaultValue=""
              rules={{ required: "Explanation is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  disabled={isDisabled}
                  label="Provide an explanation (Year)"
                  fullWidth
                  variant="outlined"
                  error={!!errors.bankruptExplanation}
                  helperText={errors.bankruptExplanation?.message}
                />
              )}
            />
          </Grid>
        )}

        <Grid size={{ xs: 12 }}>
          <Typography>Any lawsuits, judgments, or liens against you?</Typography>
          <Controller
            name="lawsuits"
            control={control}
            defaultValue="No"
            render={({ field }) => (
              <RadioGroup disabled={isDisabled} {...field} row>
                <FormControlLabel disabled={isDisabled} value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel disabled={isDisabled} value="No" control={<Radio />} label="No" />
              </RadioGroup>
            )}
          />
        </Grid>

        {watch("lawsuits") === "Yes" && (
          <Grid size={{ xs: 12 }}>
            <Controller
              name="lawsuitsExplanation"
              control={control}
              defaultValue=""
              rules={{ required: "Explanation is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  disabled={isDisabled}
                  label="Provide an explanation"
                  multiline
                  minRows={2}
                  maxRows={6}
                  fullWidth
                  variant="outlined"
                  error={!!errors.lawsuitsExplanation}
                  helperText={errors.lawsuitsExplanation?.message}
                />
              )}
            />
          </Grid>
        )}

        <Grid size={{ xs: 12 }}>
          <Typography>Has application for this bond been declined by another surety?</Typography>
          <Controller
            name="declinedSurety"
            control={control}
            defaultValue="No"
            render={({ field }) => (
              <RadioGroup disabled={isDisabled} {...field} row>
                <FormControlLabel disabled={isDisabled} value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel disabled={isDisabled} value="No" control={<Radio />} label="No" />
              </RadioGroup>
            )}
          />
        </Grid>

        {watch("declinedSurety") === "Yes" && (
          <Grid size={{ xs: 12 }}>
            <Controller
              name="suretyExplanation"
              control={control}
              defaultValue=""
              rules={{ required: "Explanation is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Provide an explanation"
                  disabled={isDisabled}
                  multiline
                  minRows={2}
                  maxRows={6}
                  fullWidth
                  variant="outlined"
                  error={!!errors.suretyExplanation}
                  helperText={errors.suretyExplanation?.message}
                />
              )}
            />
          </Grid>
        )}

        <Grid size={{ xs: 12 }}>
          <Typography>Is this bond replacing a previous bond?</Typography>
          <Controller
            name="replacingBond"
            control={control}
            defaultValue="No"
            render={({ field }) => (
              <RadioGroup disabled={isDisabled} {...field} row>
                <FormControlLabel disabled={isDisabled} value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel disabled={isDisabled} value="No" control={<Radio />} label="No" />
              </RadioGroup>
            )}
          />
        </Grid>

        {watch("replacingBond") === "Yes" && (
          <>
            <Grid size={{ xs: 12 }}>
              <Controller
                name="bondReplacementReason"
                control={control}
                defaultValue=""
                rules={{ required: "Explanation is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Provide surety name and reason for change"
                    disabled={isDisabled}
                    multiline
                    minRows={2}
                    maxRows={6}
                    fullWidth
                    variant="outlined"
                    error={!!errors.bondReplacementReason}
                    helperText={errors.bondReplacementReason?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Controller
                name="doe"
                control={control}
                defaultValue=""
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Date of Expiration of Current Bond"
                    disabled={isDisabled}
                    type="date"
                    slotProps={{ inputLabel: { shrink: true } }}
                    fullWidth
                    variant="outlined"
                  />
                )}
              />
            </Grid>
          </>
        )}

        {showBondTerm && (
          <Grid size={{ xs: 12 }}>
            <Typography>Which bond term would you like?</Typography>
            <Controller
              name="bondTerm"
              control={control}
              defaultValue={bondTermOptions[0]?.value || "1 Year Bond"}
              render={({ field }) => (
                <RadioGroup disabled={isDisabled} {...field} row>
                  {bondTermOptions.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      value={option.value}
                      disabled={isDisabled}
                      control={<Radio />}
                      label={
                        <>
                          <Typography component="span" sx={{ fontWeight: "bold" }}>
                            {option.price}
                          </Typography>{" "}
                          ({option.value})
                        </>
                      }
                    />
                  ))}
                </RadioGroup>
              )}
            />
          </Grid>
        )}
      </Grid>

      <Typography variant="h5" className="font-bold mb-4">
        {documentsHeading}
      </Typography>

      <Box className="grid grid-cols-1 gap-6">
        {documentRows.map((row) => {
          if (row.requiresCreditReportAccess && !canViewCreditReport(role, pii_access)) {
            return null;
          }
          return (
            <DocumentViewerRow
              key={row.label}
              label={row.label}
              fileName={watch(row.fileNameField)}
              fileLink={watch(row.fileLinkField)}
            />
          );
        })}
      </Box>
    </Box>
  );
};

BondRetrievalDetailedInformationBase.propTypes = {
  config: PropTypes.object.isRequired,
  isDisabled: PropTypes.bool,
  control: PropTypes.object.isRequired,
  errors: PropTypes.object,
  watch: PropTypes.func.isRequired,
  role: PropTypes.string,
  pii_access: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};

export default BondRetrievalDetailedInformationBase;
